import { denormalize, normalize } from 'normalizr'
import { useSelector } from 'react-redux'
import get from 'lodash/get'
import api from '../../api'
import { billingOrdersSchema } from '../normalizr'
import { getPaginationHeaders, useAsyncAction } from '../utils'
import createSlice from '../create-slice'
import { getEntities } from './entities'

const billingOrdersInitialState = {
  cycles: {},
  cptCodes: [],
}

const billingOrdersSlice = createSlice({
  name: 'billingOrders',
  initialState: billingOrdersInitialState,
  reducers: {
    setBillingOrders: (
      state,
      { payload: { cycleId, result, pageSize, total } },
    ) => {
      state.cycles[cycleId] = state.cycles[cycleId] || {}
      state.cycles[cycleId].ids = result
      state.cycles[cycleId].pageSize = pageSize
      state.cycles[cycleId].total = total
    },
    setCPTCodes: (state, { payload }) => {
      const existingIndex = state.cptCodes.findIndex(
        entry =>
          payload.practiceId === entry.practiceId &&
          payload.cycleId === entry.cycleId &&
          payload.from === entry.from &&
          payload.to === entry.to,
      )

      if (existingIndex >= 0) {
        state.cptCodes[existingIndex] = {
          ...state.cptCodes[existingIndex],
          ...payload,
        }
      } else {
        state.cptCodes.push(payload)
      }
    },
  },
})

const { actions } = billingOrdersSlice

const selectState = state => state[billingOrdersSlice.name]

export function useBillingOrders(cycleId) {
  const { cycles } = useSelector(selectState)
  const entities = useSelector(getEntities)
  return denormalize(
    get(cycles, [cycleId, 'ids'], []),
    [billingOrdersSchema],
    entities,
  )
}

export function useCPTCodes() {
  return useSelector(selectState).cptCodes
}

export function useBillingOrdersPagination(cycleId) {
  const { cycles } = useSelector(selectState)
  const { pageSize, total } = get(cycles, [cycleId], {})
  return { pageSize, total }
}

function fetchBillingOrders(cycleId, page, query, billed) {
  return async dispatch => {
    const { body, headers } = await api.billingOrders.list(
      cycleId,
      page,
      query,
      billed,
    )
    dispatch(
      actions.setBillingOrders({
        cycleId,
        ...normalize(body, [billingOrdersSchema]),
        ...getPaginationHeaders(headers),
      }),
    )
  }
}

export function fetchCPTCodes(practiceId, cycleId) {
  return async dispatch => {
    const { body } = await api.billingOrders.listCPTCodes(practiceId, cycleId)
    dispatch(
      actions.setCPTCodes({
        practiceId,
        cycleId,
        data: Object.keys(body)
          .sort((a, b) => a.localeCompare(b))
          .map(key => ({
            id: key,
            cptCode: key,
            amount: body[key],
          })),
      }),
    )
  }
}

export function useFetchBillingOrders(cycleId, page, query, billed) {
  return useAsyncAction(fetchBillingOrders, cycleId, page, query, billed)
}

export function useFetchCPTCodes(practiceId, cycleId) {
  return useAsyncAction(fetchCPTCodes, practiceId, cycleId)
}

export function markBillingOrderAsBilled(id, billed) {
  const type = 'MARK_AS_BILLED' // Just to update entities slice
  return async dispatch => {
    const body = await api.billingOrders.markAsBilled(id, { billed })
    return dispatch({
      type,
      payload: normalize(body, billingOrdersSchema),
    })
  }
}

export default billingOrdersSlice
